import { supabase } from '../lib/supabase'
import type { StudentDailySummary, StudentWithSummary, ClassAggregateStats, AggregatedStudentReport } from '../types/studentSummary'

export class StudentSummaryService {
  /**
   * Get the latest summary for a specific student
   */
  static async getLatestSummaryForStudent(userId: string): Promise<StudentDailySummary | null> {
    try {
      const { data, error } = await supabase
        .from('student_daily_summaries')
        .select('*')
        .eq('user_id', userId)
        .order('summary_date', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Error fetching latest summary:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching latest summary:', error)
      return null
    }
  }

  /**
   * Get summary history for a student (last N summaries)
   */
  static async getStudentSummaryHistory(userId: string, limit: number = 3): Promise<StudentDailySummary[]> {
    try {
      const { data, error } = await supabase
        .from('student_daily_summaries')
        .select('*')
        .eq('user_id', userId)
        .order('summary_date', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching summary history:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching summary history:', error)
      return []
    }
  }

  /**
   * Get summaries for multiple students (batch query)
   */
  static async getSummariesForStudents(userIds: string[]): Promise<Map<string, StudentDailySummary>> {
    try {
      if (userIds.length === 0) return new Map()

      // Get latest summary for each user
      const { data, error } = await supabase
        .from('student_daily_summaries')
        .select('*')
        .in('user_id', userIds)
        .order('summary_date', { ascending: false })

      if (error) {
        console.error('Error fetching summaries for students:', error)
        return new Map()
      }

      // Group by user_id and get only the latest for each user
      const summariesMap = new Map<string, StudentDailySummary>()
      data?.forEach(summary => {
        if (!summariesMap.has(summary.user_id)) {
          summariesMap.set(summary.user_id, summary)
        }
      })

      return summariesMap
    } catch (error) {
      console.error('Error fetching summaries for students:', error)
      return new Map()
    }
  }

  /**
   * Calculate aggregate statistics for a class/grade
   */
  static async getClassAggregate(
    students: StudentWithSummary[]
  ): Promise<ClassAggregateStats> {
    const studentsWithSummaries = students.filter(s => s.latestSummary)
    const totalStudents = students.length

    // Calculate weather distribution
    const weatherCounts = {
      sunny: 0,
      cloudy: 0,
      rainy: 0
    }

    students.forEach(student => {
      if (student.latestSummary) {
        switch (student.latestSummary.weather_icon) {
          case '☀️':
            weatherCounts.sunny++
            break
          case '☁️':
            weatherCounts.cloudy++
            break
          case '☔':
            weatherCounts.rainy++
            break
        }
      }
    })

    const weatherDistribution = {
      sunny: {
        count: weatherCounts.sunny,
        percentage: totalStudents > 0 ? Math.round((weatherCounts.sunny / totalStudents) * 100) : 0
      },
      cloudy: {
        count: weatherCounts.cloudy,
        percentage: totalStudents > 0 ? Math.round((weatherCounts.cloudy / totalStudents) * 100) : 0
      },
      rainy: {
        count: weatherCounts.rainy,
        percentage: totalStudents > 0 ? Math.round((weatherCounts.rainy / totalStudents) * 100) : 0
      }
    }

    // Calculate average video and chat counts
    const totalVideoCount = students.reduce((sum, s) => sum + s.videoCount, 0)
    const totalChatCount = students.reduce((sum, s) => sum + s.chatCount, 0)

    const averageVideoCount = totalStudents > 0 ? Math.round(totalVideoCount / totalStudents) : 0
    const averageChatCount = totalStudents > 0 ? Math.round(totalChatCount / totalStudents) : 0

    // Collect and aggregate strengths, challenges, and support strategies
    const allStrengths: string[] = []
    const allChallenges: string[] = []
    const allSupport: string[] = []
    const allStates: string[] = []

    studentsWithSummaries.forEach(student => {
      if (student.latestSummary) {
        allStrengths.push(...student.latestSummary.strengths)
        allChallenges.push(...student.latestSummary.challenges)
        allSupport.push(...student.latestSummary.teacher_support_strategy)
        allStates.push(student.latestSummary.student_state)
      }
    })

    // Get most common items (simple frequency analysis)
    const commonStrengths = this.getMostCommon(allStrengths, 3)
    const commonChallenges = this.getMostCommon(allChallenges, 3)
    const supportStrategies = this.getMostCommon(allSupport, 3)

    // Generate overall summary based on weather distribution
    let overallSummary = ''
    if (weatherCounts.sunny > weatherCounts.cloudy && weatherCounts.sunny > weatherCounts.rainy) {
      overallSummary = '挑戦意欲が高く、目標の言語化が進んでいる。自発的な学習が見られる。'
    } else if (weatherCounts.rainy > weatherCounts.sunny && weatherCounts.rainy > weatherCounts.cloudy) {
      overallSummary = 'ゴール設定が曖昧で、途中の成果が見えづらい。活動が停滞している傾向。'
    } else {
      overallSummary = '思者で脱勝ちず、AIのサポートを受け止めながら歩を形にしている。'
    }

    return {
      totalStudents,
      weatherDistribution,
      averageVideoCount,
      averageChatCount,
      overallSummary,
      commonStrengths,
      commonChallenges,
      supportStrategies
    }
  }

  /**
   * Helper function to get most common items from an array
   */
  private static getMostCommon(items: string[], limit: number): string[] {
    const frequency = new Map<string, number>()

    items.forEach(item => {
      const trimmed = item.trim()
      if (trimmed) {
        frequency.set(trimmed, (frequency.get(trimmed) || 0) + 1)
      }
    })

    return Array.from(frequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([item]) => item)
  }

  /**
   * Get the latest aggregated report for a specific scope (school/grade/class/group)
   */
  static async getAggregatedReport(
    schoolId: string,
    level: 'school' | 'grade' | 'class' | 'group',
    grade?: string,
    className?: string,
    groupId?: string
  ): Promise<AggregatedStudentReport | null> {
    try {
      let query = supabase
        .from('aggregated_student_reports')
        .select('*')
        .eq('school_id', schoolId)
        .eq('aggregation_level', level)
        .order('report_date', { ascending: false })
        .limit(1)

      if (level === 'group' && groupId) {
        query = query.eq('group_id', groupId)
      } else {
        if (grade) {
          query = query.eq('grade', grade)
        }

        if (className) {
          query = query.eq('class', className)
        }
      }

      // Fetch report and lookback_days setting in parallel
      const [reportResult, settingResult] = await Promise.all([
        query.maybeSingle(),
        supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'summary_lookback_days')
          .maybeSingle()
      ])

      if (reportResult.error) {
        console.error('Error fetching aggregate report:', reportResult.error)
        return null
      }

      if (!reportResult.data) {
        return null
      }

      // Parse lookback_days from setting (default to 28)
      let lookback_days = 28
      if (settingResult.data?.value) {
        lookback_days = parseInt(settingResult.data.value, 10)
      } else {
        console.warn('summary_lookback_days setting not found, using default value: 28')
      }

      return {
        ...reportResult.data,
        lookback_days
      }
    } catch (error) {
      console.error('Error fetching aggregate report:', error)
      return null
    }
  }

  /**
   * Export student list to CSV
   */
  static exportStudentListCSV(students: StudentWithSummary[], groupName?: string): void {
    const headers = ['学年', '組', '番号', '名前', '状況', '動画数', 'ワーク数', '要約', '良い点', '課題', '支援方針']

    const rows = students.map((student) => {
      return [
        student.grade || '-',
        student.class || '-',
        student.attendance_number || '-',
        student.name,
        student.latestSummary?.weather_icon || '-',
        student.videoCount || 0,
        student.chatCount || 0,
        student.latestSummary?.student_state || '-',
        student.latestSummary?.strengths.join('; ') || '-',
        student.latestSummary?.challenges.join('; ') || '-',
        student.latestSummary?.teacher_support_strategy.join('; ') || '-'
      ]
    })

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)

    const dateStr = new Date().toISOString().split('T')[0]
    const filename = groupName
      ? `students-${groupName}-${dateStr}.csv`
      : `students-${dateStr}.csv`

    link.download = filename
    link.click()
  }
}
